import React, { useMemo, useState } from 'react';
import _t from 'counterpart';
import {
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CTabs,
  CBadge,
} from '@coreui/react';
import { useQuery } from 'react-query';
import { getCustomerPositionsForAffiliate, getCustomerOrdersForAffiliate } from '../../services/BackendService';
import { formatCurrency, formatDateTime } from '../../helpers';
import PaginationTable, { ISorter } from '../../components/PaginationTable';
import Loading from '../../components/Loading';
import PositionTypeBadge from '../../components/PositionTypeBadge';
import PositionStatusBadge from '../../components/PositionStatusBadge';
import OrderStatusBadge, { OrderStatusType } from '../../components/OrderStatusBadge';

interface CustomerPositionsTableProps {
  customerId: string;
}

function CustomerPositionsTable({ customerId }: CustomerPositionsTableProps) {
  const [closedPage, setClosedPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<string>('closedAt|DESC');
  const [activeTab, setActiveTab] = useState<string>('open');
  const limit = 10;
  const offset = closedPage * limit - limit;

  const openPositionsQuery = useQuery(['customer-open-positions', customerId], () =>
    getCustomerPositionsForAffiliate(customerId, 0, 0, 'open')
  );

  const closedPositionsQuery = useQuery(['customer-closed-positions', customerId, offset, orderBy], () =>
    getCustomerPositionsForAffiliate(customerId, limit, offset, 'closed', orderBy)
  );

  const pendingOrdersQuery = useQuery(['customer-pending-orders', customerId], () =>
    getCustomerOrdersForAffiliate(customerId, [OrderStatusType.pending])
  );

  const count = closedPositionsQuery.data?.count || null;
  const pages = count === null ? 0 : Math.ceil(count / limit);

  const onSorterChanged = ({ column, asc }: ISorter) => {
    const sortBy = `${column}|${asc ? 'ASC' : 'DESC'}`;
    if (sortBy !== orderBy) {
      setOrderBy(sortBy);
    }
  };

  const tableFields = useMemo(
    () => [
      {
        key: 'side',
        label: _t('positions.side'),
        sorter: false,
      },
      {
        key: 'symbol',
        label: _t('positions.symbol'),
        sorter: false,
      },
      {
        key: 'openedAt',
        label: _t('positions.opened-at'),
        sorter: false,
      },
      {
        key: 'accountId',
        label: _t('positions.account-id'),
        sorter: false,
      },
      {
        key: 'id',
        label: _t('positions.external-id'),
        sorter: false,
      },
      {
        key: 'lots',
        label: _t('positions.lots'),
        sorter: false,
      },
      {
        key: 'profit',
        label: _t('positions.profit'),
        sorter: false,
        _style: { minWidth: '150px' },
      },
      {
        key: 'status',
        label: _t('positions.status'),
        sorter: false,
      },
    ],
    []
  );

  const closedPositionFields = useMemo(
    () => [
      {
        key: 'side',
        label: _t('positions.side'),
        sorter: false,
      },
      {
        key: 'symbol',
        label: _t('positions.symbol'),
        sorter: false,
      },
      {
        key: 'openedAt',
        label: _t('positions.opened-at'),
        sorter: false,
      },
      {
        key: 'accountId',
        label: _t('positions.account-id'),
        sorter: false,
      },
      {
        key: 'id',
        label: _t('positions.external-id'),
        sorter: false,
      },
      {
        key: 'lots',
        label: _t('positions.lots'),
        sorter: false,
      },
      {
        key: 'profit',
        label: _t('positions.profit'),
        sorter: false,
        _style: { minWidth: '150px' },
      },
      {
        key: 'status',
        label: _t('positions.status'),
        sorter: false,
      },
      {
        key: 'closedAt',
        label: _t('positions.closed-at'),
        sorter: false,
      },
    ],
    []
  );

  const orderTableFields = useMemo(
    () => [
      { key: 'id', label: 'ID', sorter: false },
      { key: 'externalId', label: _t('positions.orders.external-id'), sorter: false },
      { key: 'positionId', label: _t('orders.position-id'), sorter: false },
      { key: 'side', label: _t('positions.side'), sorter: false },
      { key: 'dates', label: _t('positions.orders.dates'), sorter: false },
      { key: 'quantity', label: _t('positions.orders.quantity'), sorter: false },
      { key: 'prices', label: _t('positions.orders.prices'), sorter: false },
      { key: 'status', label: _t('positions.status'), sorter: false },
    ],
    []
  );

  const openPositions = openPositionsQuery.data?.positions || [];
  const closedPositions = closedPositionsQuery.data?.positions || [];
  const pendingOrders = pendingOrdersQuery.data || [];

  if (openPositionsQuery.isLoading || closedPositionsQuery.isLoading || pendingOrdersQuery.isLoading) {
    return <Loading />;
  }

  if (openPositions.length === 0 && closedPositions.length === 0 && pendingOrders.length === 0) {
    return null;
  }

  return (
    <CRow>
      <CCol md={12}>
        <CCard className="card card--listing">
          <CCardBody className="card__holder">
            <CCardHeader className="card__header">
              <div className="card__info">
                <h4 className="card__title">{_t('positions.title')}</h4>
              </div>
            </CCardHeader>
            <CTabs activeTab={activeTab} onActiveTabChange={setActiveTab}>
              <CNav variant="tabs">
                <CNavItem>
                  <CNavLink data-tab="open">
                    {_t('positions.open')} ({openPositions.length})
                  </CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink data-tab="closed">
                    {_t('positions.closed')} ({count || 0})
                  </CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink data-tab="orders">
                    {_t('positions.orders.title')} ({pendingOrders.length})
                  </CNavLink>
                </CNavItem>
              </CNav>
              <CCard className="card--with-top-tabs">
                <CCardBody>
                  <CTabContent>
                    <CTabPane data-tab="open">
                      {openPositions.length > 0 ? (
                        <PaginationTable
                          data={openPositions}
                          tableFields={tableFields}
                          scopedSlots={{
                            side: (position: any) => (
                              <td>
                                <PositionTypeBadge position={position} />
                              </td>
                            ),
                            symbol: (position: any) => <td>{position.symbol}</td>,
                            lots: (position: any) => <td>{position?.size ? position.size : '-'}</td>,
                            profit: (position: any) => (
                              <td className="text-nowrap">
                                {position.profitLoss ? formatCurrency(position.profitLoss) : '-'}
                              </td>
                            ),
                            openedAt: (position: any) => (
                              <td className="text-nowrap">{new Date(position.openedAt).toLocaleString()}</td>
                            ),
                            accountId: (position: any) => <td className="text-nowrap">{position.accountId}</td>,
                            id: (position: any) => <td>{position.id}</td>,
                            status: (position: any) => (
                              <td>
                                <PositionStatusBadge position={position} />
                              </td>
                            ),
                          }}
                          sorter={{ column: 'openedAt', asc: false }}
                          onSorterChanged={onSorterChanged}
                          showFilter={false}
                          pagination={false}
                          loading={false}
                          pages={0}
                        />
                      ) : (
                        <p className="text-muted text-center py-3">{_t('positions.no-open-positions')}</p>
                      )}
                    </CTabPane>
                    <CTabPane data-tab="closed">
                      {closedPositions.length > 0 ? (
                        <PaginationTable
                          data={closedPositions}
                          tableFields={closedPositionFields}
                          scopedSlots={{
                            side: (position: any) => (
                              <td>
                                <PositionTypeBadge position={position} />
                              </td>
                            ),
                            symbol: (position: any) => <td>{position.symbol}</td>,
                            lots: (position: any) => <td>{position?.size ? position.size : '-'}</td>,
                            profit: (position: any) => (
                              <td className="text-nowrap">
                                {position.closedPLInAccountCurrency
                                  ? formatCurrency(position.closedPLInAccountCurrency)
                                  : '-'}
                              </td>
                            ),
                            openedAt: (position: any) => (
                              <td className="text-nowrap">{new Date(position.openedAt).toLocaleString()}</td>
                            ),
                            closedAt: (position: any) => (
                              <td className="text-nowrap">{new Date(position.closedAt).toLocaleString()}</td>
                            ),
                            accountId: (position: any) => <td className="text-nowrap">{position.accountId}</td>,
                            id: (position: any) => <td>{position.id}</td>,
                            status: (position: any) => (
                              <td>
                                <PositionStatusBadge position={position} />
                              </td>
                            ),
                          }}
                          sorter={{ column: 'closedAt', asc: false }}
                          onSorterChanged={onSorterChanged}
                          showFilter={false}
                          pagination
                          pages={pages}
                          activePage={closedPage}
                          onPageChanged={setClosedPage}
                          loading={false}
                        />
                      ) : (
                        <p className="text-muted text-center py-3">{_t('positions.no-closed-positions')}</p>
                      )}
                    </CTabPane>
                    <CTabPane data-tab="orders">
                      {pendingOrders.length > 0 ? (
                        <PaginationTable
                          data={pendingOrders}
                          tableFields={orderTableFields}
                          scopedSlots={{
                            externalId: ({ externalId }: any) => <td>{externalId || '-'}</td>,
                            dates: ({ executedAt, placedAt }: any) => (
                              <td>
                                <div>{placedAt ? formatDateTime(placedAt) : '-'}</div>
                                <div>{executedAt ? formatDateTime(executedAt) : '-'}</div>
                              </td>
                            ),
                            quantity: ({ quantity, filledQuantity }: any) => (
                              <td>
                                <div>{quantity || '-'}</div>
                                <div>{filledQuantity || '-'}</div>
                              </td>
                            ),
                            prices: ({ averagePrice, requestPrice }: any) => (
                              <td>
                                <div>{requestPrice || '-'}</div>
                                <div>{averagePrice || '-'}</div>
                              </td>
                            ),
                            side: (order: any) => (
                              <td>
                                <PositionTypeBadge position={order} />
                              </td>
                            ),
                            status: ({ status, rejectReason }: any) => (
                              <td>
                                <div>
                                  <OrderStatusBadge status={status} />
                                </div>
                                {rejectReason && <div>{rejectReason}</div>}
                              </td>
                            ),
                          }}
                          sorter={{ column: 'id', asc: false }}
                          onSorterChanged={onSorterChanged}
                          showFilter={false}
                          pagination={false}
                          loading={false}
                          pages={0}
                        />
                      ) : (
                        <p className="text-muted text-center py-3">{_t('positions.no-pending-orders')}</p>
                      )}
                    </CTabPane>
                  </CTabContent>
                </CCardBody>
              </CCard>
            </CTabs>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
}

export default CustomerPositionsTable;
